import logger from '@/app/logger';
import { useQuery } from '@tanstack/vue-query';
import axios, { AxiosError } from 'axios';
import { ref, type Ref } from 'vue';
import filters from '@/config/filters';
import { ApiUrlSchema } from '@/schemas/urlSchema';
import type { Request, Response } from 'express';
import { CustomError } from '@/utils/errors';
import type { ApiUrlType } from '@/types/schemaTypes';
import { getApiUrl } from './apiUrlBuilder';
import config from '@/config';
import { ClientError } from 'graphql-request';
import { GraphQLError } from 'graphql';

const maxLimit = filters.FILTER_DEFAULTS.limit;

export const fetchListingsQueryKey = 'listings';
export const fetchListingsQuery = async (ctx: any, validatedOpts: ApiUrlType = {}) => {
  const opts = { ...validatedOpts };
  const { pageParam = 0 } = ctx;
  opts.limit = opts?.limit || maxLimit;
  opts.offset = pageParam;
  const url = getApiUrl(opts);
  logger.info(`Fetching listings: ${url}...`);
  return axios
    .get(url)
    .then((res) => {
      const totalCount = res.data?.metaData?.totalCount;
      logger.info(`Got listings response. Count: ${totalCount}`);
      const nextCursor = totalCount > pageParam + opts.limit ? pageParam + opts.limit : undefined;
      return { ...res.data, nextCursor };
    })
    .catch((err: unknown) => {
      logger.info('Got error:', err);
      return Promise.reject(err);
    });
};

export const useListingsData = (
  optsRef: Ref<any>,
  enabledRef: Ref<boolean> = ref(true),
  excludeListingId?: Ref<string>
) => {
  const ctx = {};
  return useQuery({
    queryKey: [fetchListingsQueryKey, optsRef, excludeListingId],
    queryFn: () => fetchListingsQuery(ctx, optsRef.value),
    enabled: enabledRef,
    select: (data) => {
      if (excludeListingId?.value) {
        logger.info('Excluding listing with id: ' + excludeListingId.value);
        return {
          ...data,
          listings: data?.listings?.filter((listing: any) => listing.id !== excludeListingId.value),
        };
      } else {
        return data;
      }
    },
  });
};

export async function middlewareListingsQuery(req: Request, res: Response) {
  try {
    const results = ApiUrlSchema.safeParse(req.query);
    if (!results.success) {
      logger.info('Failed to parse api filters', results.error);
      throw new CustomError('Failed to parse api filters', 400);
    }
    const filters = results.data;
    // Ensure default sorting filters
    if (!filters.order) {
      filters.order = config.FILTER_DEFAULTS.order;
    }
    if (!filters.orderBy) {
      filters.orderBy = config.FILTER_DEFAULTS.orderBy;
    }

    const ctx = { ...(filters.offset && { pageParam: filters.offset }) };
    const data = await fetchListingsQuery(ctx, filters);
    res.send(data);
  } catch (error: any) {
    handleError(res, 'Middleware', 'Error in middlewareListingsQuery')(error);
  }
}

// This function is temporarily here because this module is called also from the client side
// To be refactored so that server code is not called from the same file as client code
function handleError(res: Response, profile: string, errorMessage: string = '') {
  return (err: Error | AxiosError | CustomError) => {
    let statusCode = axios.isAxiosError(err) && err.response?.status ? err.response.status : 500;
    statusCode = err instanceof CustomError ? err.code : statusCode;

    if (profile) {
      logger.info('Handle error with profile', { profile });
    }

    if (errorMessage) {
      logger.error(errorMessage);
    }
    if (err instanceof ClientError) {
      logger.error('GraphQL ClientError:', err);
      logger.error('GraphQL ClientError response:', err.response);
    } else if (err instanceof GraphQLError) {
      logger.error('GraphQLError: ', err);
    } else if (axios.isAxiosError(err) && err.response?.status !== 404) {
      logger.error('AxiosError:', err);
      logger.error('AxiosError response:', err.response);
    }

    res.sendStatus(statusCode);
  };
}
